import { useState } from 'react';
import { Button, FormInput, FormSelect } from '@components/UI';
import type { EntityTypeUnion } from '@einhaus/project314-types';
import type { QualifyResponse } from '@/types/appRouter';
import { trpc } from '@/api/trpc';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { showToast } from '@/components/Layout/Toast';
import { entityTypeOptions } from '../utils/screenerEditorUtils';

interface PreviewSectionProps {
    screenerRuleId: number;
}

const PreviewSection = ({ screenerRuleId }: PreviewSectionProps) => {
    const [entityType, setEntityType] = useState<EntityTypeUnion['entityType']>('Asset');
    const [entityId, setEntityId] = useState('');
    const [asOfTimestamp, setAsOfTimestamp] = useState('');
    const [response, setResponse] = useState<QualifyResponse | null>(null);
    const [errorMessages, setErrorMessages] = useState<string[]>([]);

    const qualifyMutation = useTrpcMutation(trpc.screener.previewQualify.mutationOptions({}), {
        successMessage: 'Preview complete',
        fallbackErrorMessage: 'Failed to preview screener'
    });

    const handleRun = async () => {
        const parsedEntityId = Number(entityId);

        if (!Number.isInteger(parsedEntityId) || parsedEntityId <= 0) {
            showToast('Enter a valid Entity ID', 'error');
            return;
        }

        const ts = asOfTimestamp.trim().length ? Number(asOfTimestamp.trim()) : 0;

        if (!Number.isInteger(ts) || ts < 0) {
            showToast('As-Of Timestamp must be an integer >= 0', 'error');
            return;
        }

        try {
            const result = await qualifyMutation.mutateAsync({
                screenerRuleId,
                entityId: parsedEntityId,
                entityType,
                asOfTimestamp: ts === 0 ? undefined : ts
            });

            setResponse(result.response);
            setErrorMessages(result.errorMessages ?? []);
        } catch {
            // Toast handled by useTrpcMutation
        }
    };

    return (
        <div>
            <div className="text-sm text-zinc-400 mb-3">Test a single entity against the screener rules (fail-closed).</div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <FormSelect
                    label="Entity Type"
                    options={entityTypeOptions.map((o) => ({ value: String(o.value), label: o.label }))}
                    value={entityType}
                    onChange={(e) => setEntityType(e.target.value as EntityTypeUnion['entityType'])}
                />
                <FormInput label="Entity ID" placeholder="e.g., 123" value={entityId} onChange={(e) => setEntityId(e.target.value)} />
                <FormInput
                    label="As-Of Timestamp (seconds)"
                    placeholder="0 = now"
                    value={asOfTimestamp}
                    onChange={(e) => setAsOfTimestamp(e.target.value)}
                />
                <div className="flex items-end">
                    <Button disabled={qualifyMutation.isPending} onClick={handleRun}>
                        {qualifyMutation.isPending ? 'Running...' : 'Run'}
                    </Button>
                </div>
            </div>

            {errorMessages.length > 0 && (
                <div className="mt-3 text-xs text-red-400">
                    {errorMessages.map((m, idx) => (
                        <div key={idx}>{m}</div>
                    ))}
                </div>
            )}

            {response && (
                <div className="mt-4">
                    <div className="flex items-center gap-3 mb-3">
                        <span
                            className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                                response.qualified
                                    ? 'bg-green-900/50 text-green-300 border border-green-700'
                                    : 'bg-red-900/50 text-red-300 border border-red-700'
                            }`}
                        >
                            {response.qualified ? 'Qualified' : 'Not Qualified'}
                        </span>
                        <span className="text-xs text-zinc-400">as-of: {response.asOfTimestampUsed}</span>
                    </div>

                    {response.missingDataKeys.length > 0 && (
                        <div className="text-xs text-zinc-400 mb-2">Missing data keys: {response.missingDataKeys.join(', ')}</div>
                    )}

                    {response.failedConditionIds.length > 0 && (
                        <div className="text-xs text-zinc-400 mb-2">Failed condition IDs: {response.failedConditionIds.join(', ')}</div>
                    )}

                    <details className="mt-2">
                        <summary className="cursor-pointer text-xs text-zinc-400 hover:text-zinc-300">Raw JSON</summary>
                        <pre className="mt-2 text-xs bg-zinc-900/40 border border-zinc-700 rounded p-3 overflow-auto max-h-[300px]">
                            {JSON.stringify(response, null, 2)}
                        </pre>
                    </details>
                </div>
            )}
        </div>
    );
};

export default PreviewSection;
